import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import { object, string } from 'yup';

import { GET_ORGANIZATION_IMPLEMENTATIONS } from 'packages/implementations/requests/implementations.gql';
import {
  useCreateImplementationMutation,
  useVisibleOrganizationsQuery,
} from '~/core/routes/settings/implementations/implementations.gql.generated';
import {
  SearchSelectFormField,
  TextInputFormField,
} from '~/packages/common-frontend/components/FormField';
import { Icon } from '~/packages/common-frontend/components/icons';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';

export const AddImplementationButton = ({
  ownerOrganizationId,
}: {
  ownerOrganizationId: string;
}) => {
  const toast = useToast();

  const [
    createImplementation,
    { loading: createImplementationLoading },
  ] = useCreateImplementationMutation({
    refetchQueries: [
      {
        query: GET_ORGANIZATION_IMPLEMENTATIONS,
        variables: { organizationId: ownerOrganizationId },
      },
    ],
    awaitRefetchQueries: true,
  });

  const visibleOrganizationsQuery = useVisibleOrganizationsQuery({
    variables: { organizationId: ownerOrganizationId },
  });
  if (visibleOrganizationsQuery.error) throw visibleOrganizationsQuery.error;

  const organizationOptions = useMemo(
    () =>
      visibleOrganizationsQuery.data?.organization.visibleOrganizations.map(
        (org) => ({
          label: org.name,
          value: org.id,
        }),
      ),
    [visibleOrganizationsQuery.data],
  );

  const modalControls = useDisclosure();

  return (
    <>
      <Modal {...modalControls}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Add Implementation
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            {!organizationOptions ? (
              <Skeleton />
            ) : (
              <Formik
                onSubmit={async (data) => {
                  try {
                    await createImplementation({
                      variables: {
                        ownerOrganizationId,
                        implementationName: data.implementationName,
                        borrowerId: data.borrowerId,
                      },
                    });
                    modalControls.onClose();
                  } catch (err: any) {
                    const config = {
                      ...errorToastConfig,
                      title: getUnexpectedErrorMessage(err),
                    };
                    toast(config);
                  }
                }}
                initialValues={{ implementationName: '', borrowerId: '' }}
                validationSchema={object().shape({
                  implementationName: string().required(),
                  borrowerId: string().required().uuid(),
                })}
              >
                {(formik) => (
                  <Form onSubmit={formik.handleSubmit}>
                    <VStack spacing="1rem">
                      <TextInputFormField
                        label="Implementation Name"
                        name="implementationName"
                      />
                      <SearchSelectFormField
                        label="Borrower Organization"
                        name="borrowerId"
                        options={organizationOptions}
                      />
                      <Button
                        type="submit"
                        isLoading={createImplementationLoading}
                      >
                        Add Implementation
                      </Button>
                    </VStack>
                  </Form>
                )}
              </Formik>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <Button
        onClick={modalControls.onOpen}
        variant="outline"
        rightIcon={<Icon icon="circle-plus" />}
        iconSpacing="3.125rem"
      >
        Add Implementation
      </Button>
    </>
  );
};
